import React, {useState, useRef, useEffect, RefObject} from 'react';
import {FocusTrapZone, Icon, IconButton, ITextFieldProps, Label, Layer, PrimaryButton, TextField, TooltipHost} from '@fluentui/react';
import RichTextEditor, { EditorValue } from 'react-rte';
import './RichTextField.css';
import MarkdownView from "react-showdown";
import ToggleField from "./ToggleField";
import TokenAutosuggestField from './TokenAutosuggestField';
import {ITokenSuggestion} from '../Storefront/useOpportunityTokens';
import {Css} from "nano-css/addon/vcssom/cssToTree";
import { Editor, wordCountPlugin} from 'lilac-editor';
import {SettingId} from '../../settings/registry';

interface RichTextFieldProps extends Omit<ITextFieldProps, 'onChange' | 'value'> {
    value?: string;
    readOnly?: boolean;
    onChange: (value: string) => void;
    richTextLabel?: string;
    mode?: 'html' | 'markdown'; // Add mode prop
    toolTip?: string;
    rawOnly?: boolean;
    previewStyle?: Css;
    ref?: RefObject<HTMLDivElement>;
    minHeight?: number;
    labelActions?: React.ReactNode;
    /**
     * Rendered in the editor overlay's own toolbar. The overlay is a full-screen mask with its own
     * focus trap, so anything in `labelActions` is behind it and unreachable while editing -- which
     * is exactly when a token reference is wanted. Pass it here as well to have it available there.
     */
    editorActions?: React.ReactNode;
    /**
     * {{{token}}} suggestions offered as the author types an opening brace.
     *
     * Applies to the SOURCE view only. The two WYSIWYG editors behind this component (react-rte for
     * markdown, lilac for html) expose only content in and content out through the props used here —
     * no caret or selection — so a caret-anchored popup would mean reaching into their internals.
     * The Show Source view is a plain text field, where it is both easy and exactly where anyone
     * typing a literal {{{token}}} into an HTML body already is. `editorActions` covers the other
     * views with a copy-paste list.
     */
    tokenSuggestions?: ITokenSuggestion[];
    /** Registry identifier, so documentation can deep-link to this field. */
    settingId?: SettingId;
}

/**
 * The part of a value that can safely be previewed inline.
 *
 * Email templates are frequently whole HTML documents — the stock order confirmation is one. The
 * preview renders through react-showdown, which turns the markup into React elements, and React
 * throws outright on a void element carrying children ("meta is a void element tag..."), taking the
 * page down with it. Only the body is worth previewing anyway; <head> renders as nothing.
 *
 * Falls back to stripping the document-level tags when there is no <body>, so a fragment that merely
 * starts with a stray <meta> or <style> is still previewable rather than fatal.
 */
export function previewableHtml(value: string): string {
    const body = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(value);
    const inner = body ? body[1] : value;
    return inner
        .replace(/<!DOCTYPE[^>]*>/gi, '')
        .replace(/<\/?(?:html|head|body)[^>]*>/gi, '')
        .replace(/<(?:meta|link|base)[^>]*>/gi, '')
        .replace(/<title[\s\S]*?<\/title>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .trim();
}

const RichTextField: React.FC<RichTextFieldProps> = ({ value, onChange, richTextLabel, mode = 'html', toolTip, rawOnly, previewStyle, ref, readOnly, labelActions, editorActions, tokenSuggestions, settingId, ...textFieldProps }) => {
    const [showEditor, setShowEditor] = useState(false);
    const [editorValue, setEditorValue] = useState<EditorValue>(RichTextEditor.createValueFromString(value || '', mode));
    const [rawValue, setRawValue] = useState<string>(value || '');
    const overlayRef = useRef<HTMLDivElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);
    const ownEditorContainerRef = useRef<HTMLDivElement>(null);
    const editorContainerRef = ref ?? ownEditorContainerRef;
    const [rawTextMode, setRawTextMode] = useState<boolean>(false);
    // Only sync editor value from prop when opening the modal or mode changes
    React.useEffect(() => {
        if (showEditor) {
            if(rawOnly){
                setRawTextMode(true);
                setRawValue(value || '');
            }
            else if (mode === 'html') {
                setRawValue(value || '');
            } else {
                setEditorValue(RichTextEditor.createValueFromString(value || '', 'markdown'));
            }
        }
    }, [showEditor, mode, value]);
    

    const label = () => {
        if (toolTip) {
            return (<TooltipHost content={<MarkdownView markdown={toolTip}/>}> 
                <div style={{display: "inline-flex"}} onClick={e => e.stopPropagation()}>
                    <Label>
                        <Icon iconName={'Info'} style={{paddingRight: '5px'}}/>{textFieldProps.label}
                        {labelActions}
                    </Label>
                </div>
            </TooltipHost>)
        }
        return (
            <div style={{display: "inline-flex"}} onClick={e => e.stopPropagation()}>
                <Label>{textFieldProps.label}</Label>
                {labelActions}
            </div>)
    }
    
    const handleTextFieldFocus = () => {
        if(!readOnly) {
            setShowEditor(true);
        }
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === overlayRef.current) {
            setShowEditor(false);
            if(rawTextMode) {
                onChange(rawValue);
            }
            if (mode === 'html') {
                onChange(rawValue);
            } else {
                onChange(editorValue.toString('markdown'));
            }
        }
    };
    
    const getPreviewText = () => {
        if (!value && textFieldProps.placeholder) {
            return `*${textFieldProps.placeholder}*`;
        }
        if(!value && readOnly){
            return 'Not set'
        }
        if (!value) {
            return '*Click to edit*';
        }
        return previewableHtml(value);
    }
    
    React.useEffect(() => {
        if (showEditor && editorContainerRef.current) {
            // Find the contenteditable element and focus it
            const editable = editorContainerRef.current.querySelector('[contenteditable="true"]') as HTMLElement | null;
            if (editable) {
                editable.focus();
            }
        }
    }, [showEditor]);
    
    // Block background scroll when modal is open
    useEffect(() => {
        if (showEditor) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [showEditor]);

    const applyAndClose = () => {
        if(rawTextMode) {
            setShowEditor(false);
            onChange(rawValue);
        }
        else if (mode === 'html') {
            onChange(rawValue);
        } else {
            onChange(editorValue.toString('markdown'));
        }
        setRawValue('')
        setRawTextMode(false)
        setShowEditor(false);
    }
    
    const close = () => {
        setRawTextMode(false)
        setShowEditor(false);
    }
    return (
        <React.Fragment>
            <div data-setting-id={settingId} onClick={handleTextFieldFocus} ref={previewRef} style={{width: '100%', height: '100%'}}>
                {label()}
                <MarkdownView className={(readOnly ? 'rich-text-preview-readonly' : 'rich-text-preview')} markdown={getPreviewText()} style={{...previewStyle}} />
            </div>
            {/* Render the fixed-position modal inside a Fluent <Layer>, which does two jobs:
                (1) it portals the content to a body-level host, so an ancestor with clip-path
                (e.g. the section editor's edge-to-edge background band) or transform cannot clip
                the overlay to that ancestor's box, and (2) every Fluent Panel, Modal and Dialog
                renders into a Layer at the same shared z-index, and layers paint in mount order,
                so a Layer opened from inside a hosting Panel lands on top of that Panel. That is
                how a Fluent Dialog stacks over a Panel, and it means no hardcoded z-index is
                needed here (nor would one work: Fluent's layer z-index is 1000000). */}
            {showEditor && (
                <Layer>
                <div
                    ref={overlayRef}
                    className="rtf-modal-overlay"
                    onMouseDown={handleOverlayClick}
                    onKeyDown={(event) => {
                        if(event.key === 'Escape') {
                            // Don't let Escape bubble to a hosting Panel/Modal and close it too.
                            event.stopPropagation();
                            applyAndClose()
                        }
                    }}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'auto',
                    }}
                >
                    {/* The overlay renders in its own Layer, outside any hosting Panel/Modal's
                        FocusTrapZone. Without a trap of its own, the host's trap would steal
                        focus back and the editor couldn't be typed in. Mounting a FocusTrapZone
                        here puts this overlay on top of Fluent's focus-trap stack, so the
                        innermost trap (this one) wins. */}
                    <FocusTrapZone isClickableOutsideFocusTrap={true}>
                    <div
                        className="rtf-modal-content"
                        style={undefined}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()} // Add this line
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3em' }}>
                            <IconButton
                                label={'Clear'}
                                title={'Clear'}
                                text={'Clear'}
                                iconProps={{iconName: 'delete'}}
                                onClick={() => {
                                    setEditorValue(RichTextEditor.createValueFromString('', mode))
                                    setRawValue('');
                                    setShowEditor(false);
                                }}
                            />
                            <ToggleField
                                inline={true}
                                disabled={rawOnly}
                                label="Show Source"
                                checked={rawTextMode}
                                onChange={(e, checked) => setRawTextMode(checked === true)}
                            />
                            {editorActions}
                        </div>
                        <button
                            className="rtf-modal-close"
                            aria-label="Close editor"
                            onClick={() => {
                                close()
                            }}
                            type="button"
                        >
                            ×
                        </button>
                        {richTextLabel && <div style={{ marginBottom: 8, fontWeight: 600 }}>{richTextLabel}</div>}
                        <div ref={editorContainerRef} style={{paddingTop: '1em', width: '100%'}}>
                            <div>
                            {rawTextMode && (tokenSuggestions?.length
                                ? <TokenAutosuggestField
                                    multiline
                                    value={rawValue}
                                    onChange={setRawValue}
                                    suggestions={tokenSuggestions}
                                    styles={{ root: { width: '100%' }, fieldGroup: { minHeight: '500px' } }}
                                  />
                                : <TextField
                                    multiline={true}
                                    value={rawValue}
                                    onChange={(e, v) => setRawValue(v || '')} styles={{ root: { width: '100%' }, fieldGroup: { minHeight: '500px' } }} />
                            )}
                            {!rawTextMode && mode === 'markdown' &&
                                <RichTextEditor
                                    value={editorValue}
                                    onChange={setEditorValue}
                                    rootStyle={{ minHeight: '500px' }}
                                />
                            }
                            {!rawTextMode && mode === 'html' &&
                                <Editor
                                    initialContent={rawValue}
                                    onChange={setRawValue}
                                    plugins={[
                                        wordCountPlugin,    // Document statistics panel
                                    ]}
                                    toolbar={{
                                        show: true,
                                        tools: ['bold', 'italic', 'underline', 'heading1', 'heading2', 'heading3', 'bulletList', 'orderedList', 'link', 'image'],
                                }}
                                />
                            }
                            </div>
                        </div>

                        <PrimaryButton text={'Apply'} onClick={applyAndClose} style={{marginTop: '1em'}} />
                    </div>
                    </FocusTrapZone>

                </div>
                </Layer>
            )}
        </React.Fragment>
    );
};

export default RichTextField;
